import { useEffect, useRef, useCallback } from 'react'

interface Options {
  enabled: boolean
  onClose: () => void
  // 'right' for drawers that slide in from the right (close gesture = swipe
  // right). 'left' for drawers that slide in from the left (close gesture =
  // swipe left). Defaults to 'right' for backwards compatibility.
  placement?: 'right' | 'left'
  // Width of the edge zone (in px) that captures the close gesture. The
  // zone sits above the scrollable body so it claims the gesture before
  // the body's native scroll can.
  edgeSize?: number
}

// Finger-follow swipe-to-close for side-placed drawers. Returns two refs:
//   - drawerRef: attach to the element you want to translate. For Chakra
//     Drawer use `<DrawerContent ref={drawerRef}>`.
//   - edgeZoneRef: attach to a transparent overlay positioned along the
//     drawer's INNER edge — i.e. the edge opposite the placement. For a
//     `placement='right'` drawer the zone sits on the left edge of the
//     drawer; for `placement='left'` it sits on the right edge.
//
// On a horizontal swipe in the close direction past threshold the drawer
// is animated off-screen and `onClose()` fires after the slide finishes.
// Under threshold it snaps back. If iOS native swipe-back fires for the
// same gesture it will pop the underlying route — callers should also
// disable native back-swipe while the drawer is open.
export const useDrawerCloseSwipe = ({
  enabled,
  onClose,
  placement = 'right',
  edgeSize: _edgeSize = 24,
}: Options) => {
  // close gesture sign: right-placed drawer closes on positive dx (swipe
  // right); left-placed closes on negative dx (swipe left).
  const sign = placement === 'right' ? 1 : -1
  const drawerRef = useRef<HTMLDivElement | null>(null)
  const edgeZoneRef = useRef<HTMLDivElement | null>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const setDragTransform = useCallback((px: number, animated: boolean) => {
    const el = drawerRef.current
    if (!el) return
    el.style.transition = animated
      ? 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)'
      : 'none'
    if (px === 0) {
      el.style.transform = ''
      el.style.willChange = ''
    } else {
      el.style.willChange = 'transform'
      el.style.transform = `translateX(${px}px)`
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    type State = {
      startX: number
      startY: number
      startT: number
      locked: 'h' | 'v' | null
      width: number
    } | null
    let state: State = null
    let attached = false
    let zoneEl: HTMLDivElement | null = null
    let cancelled = false

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0]
      if (!t) return
      const drawer = drawerRef.current
      state = {
        startX: t.clientX,
        startY: t.clientY,
        startT: performance.now(),
        locked: null,
        width: drawer?.clientWidth || window.innerWidth,
      }
    }

    const onMove = (e: TouchEvent) => {
      if (!state) return
      const t = e.touches[0]
      if (!t) return
      const dx = t.clientX - state.startX
      const dy = t.clientY - state.startY

      if (state.locked === null) {
        const absX = Math.abs(dx)
        const absY = Math.abs(dy)
        if (absX < 6 && absY < 6) return
        state.locked = absX > absY ? 'h' : 'v'
        if (state.locked === 'v') {
          state = null
          setDragTransform(0, true)
          return
        }
      }

      if (state.locked !== 'h') return
      if (e.cancelable) e.preventDefault()
      // Only allow dragging in the close direction (sign), and clamp the
      // opposite direction to 0 (drawer doesn't overshoot its rest pose).
      const signedDx = sign === 1 ? Math.max(0, dx) : Math.min(0, dx)
      setDragTransform(signedDx, false)
    }

    const onEnd = () => {
      const s = state
      if (!s) return
      state = null
      const drawer = drawerRef.current
      if (s.locked !== 'h' || !drawer) {
        setDragTransform(0, true)
        return
      }
      const matrix = new DOMMatrix(getComputedStyle(drawer).transform)
      const currentX = matrix.m41
      const elapsed = performance.now() - s.startT
      // Magnitude in the close direction
      const closeMagnitude = sign === 1 ? currentX : -currentX
      const velocity = elapsed > 0 ? closeMagnitude / elapsed : 0
      const shouldClose =
        closeMagnitude > s.width * 0.3 || (velocity > 0.5 && closeMagnitude > 40)
      if (shouldClose) {
        setDragTransform(sign * s.width, true)
        window.setTimeout(() => onCloseRef.current(), 220)
      } else {
        setDragTransform(0, true)
      }
    }

    const attach = (el: HTMLDivElement) => {
      zoneEl = el
      attached = true
      el.addEventListener('touchstart', onStart, { passive: false })
      el.addEventListener('touchmove', onMove, { passive: false })
      el.addEventListener('touchend', onEnd)
      el.addEventListener('touchcancel', onEnd)
    }

    // The edge zone may not be in the DOM on the same tick the effect runs
    // (the drawer's Portal-rendered content commits after this). Poll
    // briefly until it appears.
    const initial = edgeZoneRef.current
    if (initial) {
      attach(initial)
    } else {
      const tryAttach = () => {
        if (cancelled) return
        const el = edgeZoneRef.current
        if (el) {
          attach(el)
        } else {
          requestAnimationFrame(tryAttach)
        }
      }
      requestAnimationFrame(tryAttach)
    }

    return () => {
      cancelled = true
      if (attached && zoneEl) {
        zoneEl.removeEventListener('touchstart', onStart)
        zoneEl.removeEventListener('touchmove', onMove)
        zoneEl.removeEventListener('touchend', onEnd)
        zoneEl.removeEventListener('touchcancel', onEnd)
      }
      state = null
    }
  }, [enabled, setDragTransform, sign])

  return { drawerRef, edgeZoneRef }
}
